import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from './cn';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOverlayClick?: boolean;
  /**
   * The close ✕ in the header. Defaults ON.
   *
   * Escape and a backdrop click already closed this, but neither is VISIBLE —
   * an operator who does not already know them is stuck looking at a dialog
   * with no way out, and "click outside" is not discoverable on a panel that
   * fills the screen. Opt out only where the dialog must be answered rather
   * than dismissed (a destructive confirm, say), and give that dialog an
   * explicit Cancel instead.
   */
  showCloseButton?: boolean;
  /**
   * Horizontal alignment of the title/description header block.
   *
   * Added 2026-08-27. Eight call sites in the US CRM (`ApproveContractButton`,
   * `AssignBrokerPanel`, `ChangeQuoteStatusButton`, `MeterSummaryCard`,
   * `PlanEflView`, `QuoteDocumentDownloads`, `QuoteSummaryCards`) were already
   * passing `headerAlign="center"` on `dev` against a prop that had never
   * existed in this package — not in `src`, not in `dist`, not in any commit.
   * Someone developed against a local Dialog change that was never pushed, so
   * `dev` did not typecheck against published Common.
   *
   * Defaults to `'left'`, which is the previous — and only — behaviour, so
   * every existing call site across all six country apps renders unchanged.
   */
  headerAlign?: 'left' | 'center';
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  // For forms that are genuinely wide — three columns of fields rather than
  // two. At `lg` a ten-field form runs to five stacked rows and the dialog
  // becomes taller than the viewport it is centred in, which is the one shape
  // the height cap below cannot rescue.
  xl: 'max-w-4xl',
} as const;

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  showCloseButton = true,
  headerAlign = 'left',
}: DialogProps): JSX.Element | null {
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previous;
    };
    // Depend ONLY on `open`. Bundling `handleKey` here (it changes every render
    // because consumers pass an inline `onClose`) re-ran this effect on every
    // keystroke, and `dialogRef.focus()` yanked focus off the active input — so
    // typing in a dialog field "deselected" after one character. Focus must
    // happen once, on open.
  }, [open]);

  // Escape-to-close listener — separate effect so re-binding it when the
  // handler identity changes never re-triggers the focus-on-open effect above.
  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, handleKey]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50 p-4"
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        // ⚠️ HEIGHT-CAPPED AND COLUMN-FLEXED ON PURPOSE — added 2026-08-29.
        //
        // This panel had no height constraint, and the open effect above sets
        // `document.body.style.overflow = 'hidden'`. So a dialog taller than the
        // viewport was unreachable in BOTH directions: it overflowed off the top
        // and bottom, and the page behind it could not scroll either. The CRM's
        // offer composer hit this — plan list, ordered preview, account picker
        // and recipient field stack past any laptop screen, and the Send button
        // in the footer could not be reached at all.
        //
        // `100dvh` not `100vh`: on mobile Safari `vh` includes the retracting
        // browser chrome, so a `100vh` panel is taller than what you can see.
        // `min-h-0` on the body is what actually lets it shrink — a flex child
        // defaults to `min-height:auto` and refuses to become smaller than its
        // content, which silently defeats `overflow-y-auto`.
        className={cn(
          'flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded-lg border border-border bg-bg shadow-lg outline-none',
          sizeClasses[size],
        )}
      >
        {(title || description) && (
          <div
            className={cn(
              // `shrink-0` so a long body can never squeeze the title away.
              'relative shrink-0 border-b border-border p-4',
              headerAlign === 'center' && 'text-center',
            )}
          >
            {title && (
              // Padded away from the ✕ so a long title wraps before it reaches
              // the button rather than running underneath it. Applied only when
              // the button is there, so a dialog without one keeps full width.
              <h2
                id={titleId}
                className={cn(
                  'text-lg font-semibold text-foreground',
                  showCloseButton && 'pr-8',
                )}
              >
                {title}
              </h2>
            )}
            {description && (
              <p
                id={descId}
                className={cn('mt-1 text-sm text-muted', showCloseButton && 'pr-8')}
              >
                {description}
              </p>
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close dialog"
                data-testid="dialog-close"
                className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full text-muted transition-colors hover:bg-border/60 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {/* Inline SVG, not a glyph: ✕ and × render at different sizes
                    and baselines per font, so the button would sit visibly
                    off-centre on whichever platform lacked the metric. */}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M2 2l10 10M12 2L2 12" />
                </svg>
              </button>
            )}
          </div>
        )}
        {/* The ONLY scrolling region. `min-h-0` is load-bearing — see the note
            on the panel above. Header and footer stay pinned, so the primary
            action is always reachable however long the body gets. */}
        {/* `scrollbar-gutter: stable` keeps the left and right padding EQUAL.
            Without it the scrollbar appears inside the right-hand padding the
            moment the body overflows, so a tall dialog's content sits visibly
            off-centre — and shifts sideways as content grows past the fold,
            which reads as a layout bug rather than as a scrollbar. */}
        {children && (
          <div className="min-h-0 flex-1 overflow-y-auto p-4 [scrollbar-gutter:stable]">
            {children}
          </div>
        )}
        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border p-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
